import { pipe } from 'fp-ts/function';
import isFunction from 'lodash-es/isFunction';
import isNil from 'lodash-es/isNil';
import isObject from 'lodash-es/isObject';
import isString from 'lodash-es/isString';
import { lowerCasePropertyNames } from '../utils';
import { abortTimeout } from './abort';
import { FETCH_REQUEST_EVENT } from './events';
import * as Url from '../Url';
import { HttpResponseException } from './exceptions/HttpResponseException';
import { DELETE, Method, PATCH, POST, PUT, methodAllowsBody, } from './methods';
import { HTML_MIME, JSON_MIME, TEXT_PLAIN_MIME, URLENCODED_MIME, } from './mimes';
import { UNKNOWN, isKnownStatus, isSuccessStatus, statusHasNoContent, FORBIDDEN, } from './status';
import { MFA } from './error';
import { showMFAScreen, subscribeToOnMFAResult, MFAResult, } from '../EventBus';
let commonConfig = {};
/**
 * Consumer apps have the ability to set their own defaults for the HTTP client.
 *
 * @example
 * import { Http } from '@catawiki/runtime-toolkit';
 *
 * Http.setDefaultConfig({
 *   credentials: 'include',
 *   headers: {
 *     'X-App-Name': 'my-app',
 *   },
 * });
 *
 * Every subsequent request will now have the credentials set to 'include' and the 'X-App-Name' header set to 'my-app'
 * unless explicitly overridden.
 */
export const configure = (newConfig) => {
    if (newConfig) {
        if (newConfig.headers) {
            commonConfig = {
                ...newConfig,
                headers: lowerCasePropertyNames(newConfig.headers || {}),
            };
        }
        else {
            commonConfig = { ...newConfig };
        }
    }
};
const defaultBodyParser = (contentType) => {
    // Check against the first part of the content type
    switch (contentType.split(/;\s*/)[0]) {
        case HTML_MIME:
        case TEXT_PLAIN_MIME:
            return 'text';
        case JSON_MIME:
            return 'json';
        default:
            return 'blob';
    }
};
// Name of the header added by Nginx
export const GEO_HEADER = 'x-geo-country-iso-code';
/**
 * Creates a configuration object for a basic HTTP request with the given method
 * and to the given url.
 *
 * Use shorthand [Methods](#methods) instead.
 */
export const createRequest = (method, url) => ({
    bodyParser: defaultBodyParser,
    headers: { accept: JSON_MIME },
    credentials: 'same-origin',
    ...commonConfig,
    search: '',
    method,
    url,
});
/**
 * Replaces the default body parser with the given one.
 * Any of [the body parsers supported by
 * [fetch](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#body)
 * may be used.
 *
 * The default body parsers checks response Content-Type header to infer the
 * parser, and should suffice in most cases.
 *
 * @example
 * import { Http } from '@catawiki/runtime-toolkit';
 * import { pipe } from 'fp-ts/pipeable';
 *
 * pipe(
 *   Http.get('/foo/bar'),
 *   Http.setBodyParser(() => 'arrayBuffer'),
 *   Http.send<ArrayBuffer>()
 * )
 *   .then(() => {})
 *   .catch(() => {});
 */
export const setBodyParser = (bodyParser) => (config) => ({ ...config, bodyParser });
/**
 * Sets the given value as the URL search for the given request configuration.
 *
 * Uses `Url.createSearch` and internally [qs](https://github.com/ljharb/qs) to stringify the search.
 * `qs.stringify` options might be provided as the second argument.
 *
 * @example
 * import { Http } from '@catawiki/runtime-toolkit';
 * import { pipe } from 'fp-ts/pipeable';
 *
 * const config = pipe(
 *   Http.get('/foo/bar'),
 *   Http.setSearch({ name: 'Jane' }),
 * )
 *
 * assert.strictEqual(config.search, '?name=Jane')
 *
 */
export const setSearch = (value, qsOpts) => (config) => {
    if (isNil(value)) {
        return config;
    }
    const query = isString(value) ? value : Url.createSearch(value, qsOpts);
    return {
        ...config,
        search: `?${query.replace(/^\?/, '')}`,
    };
};
/**
 * Sets the headers for the given request configuration.
 *
 * **IMPORTANT**: Replaces the default headers! You might want to use
 * [addHeader](#addHeader).
 *
 * Note: headers names are lowercased to prevent unexpected behaviour when the
 * same header is present in different casing versions.
 *
 * @example
 * import { Http } from '@catawiki/runtime-toolkit';
 * import { pipe } from 'fp-ts/pipeable';
 *
 * const config = pipe(
 *   Http.get('/foo/bar'),
 *   Http.setHeaders({ 'X-Foo': 'such header much wow' }),
 * )
 *
 * assert.deepStrictEqual(config.headers, { 'x-foo': 'such header much wow' })
 */
export const setHeaders = (headers) => (config) => ({
    ...config,
    headers: {
        ...config.headers,
        ...lowerCasePropertyNames({ ...headers }),
    },
});
/**
 * Sets headers that are required for internal API calls such as in `getServerSideProps` in next.js
 *
 * `xForwardedProto` is optional, set to https by default.
 *
 * @example
 * import { Http } from '@catawiki/runtime-toolkit';
 * import { pipe } from 'fp-ts/pipeable';
 *
 * const config = pipe(
 *   Http.get('/foo/bar'),
 *   Http.setHeaders({ 'X-Foo': 'such header much wow' }),
 *   Http.setServerHeaders({
 *     host: serverRuntimeConfig.host,
 *     authToken: serverRuntimeConfig.authToken,
 *     app: '<app name>-ui'
 *   })
 * )
 */
export const setServerHeaders = ({ host, app, xForwardedProto = 'https', authToken, xGeoCountryIsoCode, }) => (config) => {
    const headers = {};
    headers.host = host;
    headers['User-Agent'] = `NodeJS/${app}/cw-runtime-toolkit/HttpClient`;
    headers['x-forwarded-proto'] = xForwardedProto;
    headers['Auth-Token'] = authToken;
    if (xGeoCountryIsoCode) {
        headers[GEO_HEADER] = xGeoCountryIsoCode;
    }
    return {
        ...config,
        headers: {
            ...config.headers,
            ...lowerCasePropertyNames({ ...headers }),
        },
    };
};
/**
 * Adds the given header name and value pair to the headers of given request
 * configuration.
 *
 * Note: headers names are lowercased to prevent unexpected behaviour when the
 * same header is present in different casing versions.
 *
 * @example
 * import { Http } from '@catawiki/runtime-toolkit';
 * import { pipe } from 'fp-ts/pipeable';
 *
 * const config = pipe(
 *   Http.get('/foo/bar'),
 *   Http.addHeader('X-Foo', 'such header much wow'),
 * )
 *
 * assert.strictEqual(config.headers['x-foo'], 'such header much wow')
 */
export const addHeader = (name, value) => (config) => ({
    ...config,
    headers: {
        ...config.headers,
        [name.toLowerCase()]: value,
    },
});
/**
 * Set headers that tell the browser to not cache the response.
 */
export const setNoCacheHeaders = (config) => pipe(config, addHeader('Cache-Control', 'no-cache, no-store, must-revalidate'), addHeader('Pragma', 'no-cache'), addHeader('Expires', '0'));
/**
 * Sets the request timeout for the given request configuration.
 * This will make fetch abort the request when the response take
 * longer than the time in milliseconds set in this setting.
 *
 * @example
 * import { Http } from '@catawiki/runtime-toolkit';
 * import { pipe } from 'fp-ts/pipeable';
 *
 * pipe(
 *   Http.get('/foo/bar'),
 *   Http.setTimeoutMs(1000)
 *   Http.send<string>()
 * )
 *   .then(() => {})
 *   .catch(() => {});
 */
export const setTimeoutMs = (timeoutMs) => (config) => ({
    ...config,
    timeoutMs,
});
/**
 * Parses the given response body based on its content type.
 */
const parseBody = (config, response) => {
    const { headers } = response;
    if (config.method === Method.HEAD) {
        return Promise.resolve(undefined);
    }
    const contentType = headers.get('content-type');
    if (contentType === null) {
        return Promise.resolve(undefined);
    }
    const parser = config.bodyParser(contentType);
    return response[parser]();
};
const hasErrorResponse = (error) => isObject(error) && Object.prototype.hasOwnProperty.call(error, 'error');
/**
 * Handles the given response by either returning nothing if its status
 * indicates it has no body or by parsing its body contents to the correct
 * format using the provided content type.
 *
 * @throws an exception with the payload if the responses status wasn't successful.
 */
const receive = async (config, response, includeHeadersInReturn, protectedWithMFA) => {
    const { status, url } = response;
    // Return nothing when the response does not contain a body
    if (!isKnownStatus(status) || statusHasNoContent(status)) {
        return undefined;
    }
    const payload = await parseBody(config, response);
    if (isSuccessStatus(status)) {
        if (includeHeadersInReturn) {
            const parsedResponseWithHeaders = {
                parsedResponse: payload,
                headers: response.headers,
            };
            return parsedResponseWithHeaders;
        }
        return payload;
    }
    else if (protectedWithMFA &&
        status === FORBIDDEN &&
        hasErrorResponse(payload) &&
        payload?.error?.code === MFA) {
        const mfaData = payload.error.meta;
        return new Promise((resolve, reject) => {
            showMFAScreen(mfaData);
            subscribeToOnMFAResult((eventData) => {
                const { detail } = eventData;
                if (detail.status === MFAResult.SUCCESS) {
                    // needed to call send() here to re-try the request
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    send(includeHeadersInReturn, true)(config)
                        .then((res) => {
                        resolve(res);
                    })
                        .catch((err) => {
                        reject(err);
                    });
                }
                else {
                    reject(new HttpResponseException(url, status, payload));
                }
            }, { once: true });
        });
    }
    else {
        throw new HttpResponseException(url, status, payload);
    }
};
/**
 * Returns if an error (of type unknown) is an error with fetch Response.
 * Note we assume it is if the object contains the "response" property
 */
const hasFetchResponse = (error) => Boolean(typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    error.response);
/**
 * Sends a request with the given configuration and waits for a response with a
 * body of the given type.
 *
 * See [http/index](/docs/modules/http/index.ts.md#index-overview) docs for
 * examples how to use.
 *
 * @param includeHeadersInReturn - if true, the response will include the headers
 *
 * @param protectedWithMFA - if true and receive the MFA error status code, it will show the MFA screen
 *
 * See [Web MFA ADR](https://catawiki.atlassian.net/wiki/spaces/FRONT/pages/1975746650/) here for more details
 *
 * @throws an exception when there is no network access.
 */
export const send = (includeHeadersInReturn, protectedWithMFA) => async (config) => {
    const { url, search, method, timeoutMs, ...rest } = config;
    const init = {
        ...rest,
        method: method.verb,
        body: methodAllowsBody(method) ? method.body : undefined,
    };
    const request = new Request(url + search, init);
    if (isFunction(globalThis.dispatchEvent)) {
        globalThis.dispatchEvent(new CustomEvent(FETCH_REQUEST_EVENT, { detail: request }));
    }
    try {
        const { timeout, signal } = abortTimeout(timeoutMs);
        const response = await fetch(request, {
            signal,
        });
        if (timeout) {
            clearTimeout(timeout);
        }
        return receive(config, response, includeHeadersInReturn, protectedWithMFA);
    }
    catch (error) {
        if (hasFetchResponse(error)) {
            const { response } = error;
            throw new HttpResponseException(url, isKnownStatus(response.status) ? response.status : UNKNOWN, response);
        }
        // Error object does not have enumerable properties https://stackoverflow.com/a/50738205
        if (error instanceof Error) {
            const payload = {
                ...error,
                name: error.name,
                message: error.message,
                stack: error.stack,
            };
            throw new HttpResponseException(url, UNKNOWN, payload);
        }
        throw new HttpResponseException(url, UNKNOWN, error);
    }
};
const requestWithBody = (method) => (url) => (body) => createRequest(Method[method](body), url);
const requestWithJsonBody = (method) => (url, body) => pipe(JSON.stringify(body), requestWithBody(method)(url), addHeader('content-type', JSON_MIME));
const requestWithUrlencodedBody = (method) => (url, body) => pipe(new URLSearchParams(body), requestWithBody(method)(url), addHeader('content-type', URLENCODED_MIME));
/**
 * Create a POST HTTP request config
 */
export const post = requestWithBody(POST);
/**
 * Create a POST HTTP request config with JSON content type.
 */
export const postJson = requestWithJsonBody(POST);
/**
 * Create a POST HTTP request config with Url encoded body
 */
export const postUrlencoded = requestWithUrlencodedBody(POST);
/**
 * Create a PATCH HTTP request config
 */
export const patch = requestWithBody(PATCH);
/**
 * Create a PATCH HTTP request config with JSON content type
 */
export const patchJson = requestWithJsonBody(PATCH);
/**
 * Create a PATCH HTTP request config with Url encoded body
 */
export const patchUrlencoded = requestWithUrlencodedBody(PATCH);
/**
 * Create a PUT HTTP request config
 */
export const put = requestWithBody(PUT);
/**
 * Create a PUT HTTP request config with JSON content type
 */
export const putJson = requestWithJsonBody(PUT);
/**
 * Create a PUT HTTP request config with Url encoded body
 */
export const putUrlencoded = requestWithUrlencodedBody(PUT);
/**
 * Create a DELETE HTTP request config
 */
export const del = requestWithBody(DELETE);
/**
 * Create a DELETE HTTP request config with JSON content type
 */
export const delJson = requestWithJsonBody(DELETE);
/**
 * Create a DELETE HTTP request config with Url encoded body
 */
export const delUrlencoded = requestWithUrlencodedBody(DELETE);
/**
 * Create a GET HTTP request config
 */
export const get = (url) => createRequest(Method.GET, url);
/**
 * Create a HEAD HTTP request config
 */
export const head = (url) => createRequest(Method.HEAD, url);
/**
 * Create an OPTIONS HTTP request config
 */
export const options = (url) => createRequest(Method.OPTIONS, url);
